// Runtime helpers for ekstra orddiktat-enheder.

import {
  DICTATION_UNITS_DA,
  DICTATION_UNITS_ENGLISH,
  DICTATION_UNIT_SEPARATOR,
  EXTRA_DICTATION_SESSION_UNITS,
  type DictationUnit,
} from '../data/curriculum/dictationUnits';
import { getTrin } from '../data/curriculum';
import { makeRng } from '../data/curriculum/generators';
import type { UserProfile } from '../types';

export {
  DICTATION_UNIT_SEPARATOR,
  EXTRA_DICTATION_SESSION_UNITS,
  type DictationUnit,
} from '../data/curriculum/dictationUnits';

/** Union af unlockedKeys fra gennemførte trin + aktivt trin. */
export function getCumulativeUnlockedKeys(user: UserProfile): string[] {
  const keys = new Set<string>();
  const trinIds = new Set([...user.completedTrinIds, user.currentTrinId]);
  for (const id of trinIds) {
    const trin = getTrin(id);
    if (!trin) continue;
    for (const k of trin.unlockedKeys) keys.add(k);
  }
  return [...keys];
}

/** Brugerpræference 1–7; 7 = 6+ (wordCount >= 7). */
export function unitLengthBucket(preference: number): {
  min: number;
  max: number;
} {
  const p = Math.max(1, Math.min(7, Math.round(preference)));
  if (p >= 7) return { min: 7, max: 99 };
  return { min: p, max: p };
}

export function textUsesOnlyKeys(text: string, keys: ReadonlyArray<string>): boolean {
  const set = new Set(keys);
  return [...text].every((c) => set.has(c));
}

export function filterUnitsForUser(
  user: UserProfile,
  unitLengthPreference: number,
): DictationUnit[] {
  const keys = getCumulativeUnlockedKeys(user);
  const { min, max } = unitLengthBucket(unitLengthPreference);
  const lexicon = user.preferences?.dictationLexicon === 'en' ? 'en' : 'da';
  const bank = lexicon === 'en' ? DICTATION_UNITS_ENGLISH : DICTATION_UNITS_DA;
  return bank.filter(
    (u) =>
      u.wordCount >= min &&
      u.wordCount <= max &&
      textUsesOnlyKeys(u.text, keys),
  );
}

export function countAvailableUnits(
  user: UserProfile,
  unitLengthPreference: number,
): number {
  return filterUnitsForUser(user, unitLengthPreference).length;
}

export function pickDictationUnits(
  user: UserProfile,
  unitLengthPreference: number,
  count = EXTRA_DICTATION_SESSION_UNITS,
  seed?: number,
): DictationUnit[] {
  const pool = filterUnitsForUser(user, unitLengthPreference);
  if (pool.length === 0) return [];

  const rng = makeRng(seed);
  return pickDiverseSession(pool, count, rng);
}

/** Join picked units into round target text. */
export function joinDictationUnits(units: DictationUnit[]): string {
  return units.map((u) => u.text).join(DICTATION_UNIT_SEPARATOR);
}

export function dictationUnitLengthLabel(preference: number): string {
  const p = Math.max(1, Math.min(7, Math.round(preference)));
  return p >= 7 ? '6+' : String(p);
}

const FUNCTION_WORDS = new Set([
  'er', 'det', 'en', 'den', 'de', 'et', 'og', 'at', 'her', 'nu',
  'jeg', 'du', 'vi', 'min', 'din', 'har', 'for', 'men', 'til',
  'om', 'i', 'på', 'af', 'med', 'som', 'der', 'te', 'to',
]);

function contentWords(text: string): string[] {
  return text.split(/\s+/).filter((w) => w && !FUNCTION_WORDS.has(w));
}

function sessionContentOverlap(a: DictationUnit, picked: DictationUnit[]): number {
  const wordsA = new Set(contentWords(a.text));
  let overlap = 0;
  for (const p of picked) {
    for (const w of contentWords(p.text)) {
      if (wordsA.has(w)) overlap++;
    }
  }
  return overlap;
}

/** Vælg enheder med minimal ord-overlap inden for sessionen. */
function pickDiverseSession(
  pool: DictationUnit[],
  count: number,
  rng: () => number,
): DictationUnit[] {
  if (pool.length <= count) {
    const shuffled = [...pool];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    const out: DictationUnit[] = [];
    for (let i = 0; i < count; i++) out.push(shuffled[i % shuffled.length]);
    return out;
  }

  const picked: DictationUnit[] = [];
  const remaining = new Set(pool);

  // Første enhed: tilfældig
  const firstArr = [...remaining];
  const first = firstArr[Math.floor(rng() * firstArr.length)];
  picked.push(first);
  remaining.delete(first);

  while (picked.length < count && remaining.size > 0) {
    let best: DictationUnit | null = null;
    let bestScore = Infinity;
    let tieCount = 0;

    for (const candidate of remaining) {
      const overlap = sessionContentOverlap(candidate, picked);
      if (overlap < bestScore) {
        bestScore = overlap;
        best = candidate;
        tieCount = 1;
      } else if (overlap === bestScore) {
        tieCount++;
        if (rng() < 1 / tieCount) best = candidate;
      }
    }

    if (!best) break;
    picked.push(best);
    remaining.delete(best);
  }

  // Fallback hvis pool er for lille til fuld diversitet
  while (picked.length < count && pool.length > 0) {
    picked.push(pool[picked.length % pool.length]);
  }

  return picked;
}
